import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';

export interface Profile {
  id: string;
  display_name: string;
  email: string; // Only available when viewing own profile
  bio?: string;
  avatar_url?: string;
  organization?: string;
  organizations?: string[];
  phone?: string; // Only available when viewing own profile
  title?: string;
  linkedin_url?: string; // Only available when viewing own profile
  industries?: any;
  username?: string;
  web3_wallet_address?: string;
  created_at: string;
  updated_at: string;
}

// Hook to get any user's profile with proper field filtering
// Uses secure database function for other users' profiles to prevent sensitive data exposure
export const usePublicProfile = (userId: string | undefined) => {
  const { user: currentUser } = useAuth();
  
  const { data: profile, isLoading } = useQuery({
    queryKey: ['profile', userId],
    queryFn: async () => {
      if (!userId) return null;
      
      const isOwnProfile = currentUser?.id === userId;
      
      // For own profile, fetch all fields directly
      if (isOwnProfile) {
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', userId)
          .single();

        if (error) throw error;
        return data as Profile;
      }
      
      // For other users, use secure function that only returns public fields
      const { data, error } = await supabase
        .rpc('get_public_profile', { p_user_id: userId });

      if (error) throw error;
      
      // Function returns array, get first result
      const publicProfile = Array.isArray(data) ? data[0] : data;
      
      if (!publicProfile) return null;
      
      // Return with empty sensitive fields to maintain type compatibility
      return {
        ...publicProfile,
        email: '', // Not returned from function
        phone: undefined, // Not returned from function
        linkedin_url: undefined, // Not returned from function
      } as Profile;
    },
    enabled: !!userId
  });

  return { profile, isLoading };
};

// Hook to get current user's own profile
export const useProfile = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: profile, isLoading } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      if (!user) return null;
      
      // Get all fields for own profile
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (error) throw error;
      return data as Profile;
    },
    enabled: !!user
  });

  const updateProfile = useMutation({
    mutationFn: async (updates: Partial<Profile>) => {
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', user.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile', user?.id] });
      toast({
        title: "Profile updated",
        description: "Your profile has been successfully updated"
      });
    },
    onError: (error: any) => {
      toast({
        title: "Update failed",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  return {
    profile,
    isLoading,
    updateProfile: updateProfile.mutate
  };
};
